import {FC, ReactElement, useEffect, useState} from 'react';
import ActivityFrame from "../activity-frame/activity-frame"
import Tabs from '../tabs/tabs';
import { TabSummary } from '../../models/TabSummary';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { Lookup } from '../../models/Lookup';
import LookupTypes from '../../models/LookupTypes';
import { getSiteNamesWithPagination } from '../../services/siteService';
import SiteSearch from '../../models/search/SiteSearch';
import AssignedTab from '../assignWizard/AssignedTab';
import AccessGroupSearch from '../../models/search/AccessGroupSearch';
import AccessGroupColumns from '../../models/ColumnDefinitions/AccessGroupColumns';
import { ListQuery } from '../../models/ListQuery';
import LiftColumns from '../../models/ColumnDefinitions/LiftColumns';
import LiftSearch from '../../models/search/LiftSearch';
import { getLifts } from '../../services/liftService';
import { getAllAccessGroups } from '../../services/accessGroupService';
import { appendFilter } from '@/utilities/filters';
import { getAccessTimePatternNames } from '@/services/accessTimePatternService';
import AccessTimePatternSearch from '@/models/search/AccessTimePatternSearch';
import { getAccessAreaNamesWithPagination } from '@/services/accessAreaService';
import AccessAreaSearch from '@/models/search/AccessAreaSearch';
import { getDayNames } from '@/utilities/DateUtils';
import { getBuildingNamesWithPagination } from '@/services/buildingService';
import BuildingSearch from '@/models/search/BuildingSearch';
import { showError } from '@/services/utilities/ServiceError';
import { getFloorTabCounts } from '@/services/floorService';
import { toast } from 'react-toastify';

interface LinkedToTabProps {
    floorId: string
}

const LinkedToTab: FC<LinkedToTabProps> = ({ floorId }) => {
    const { translate } = useLocale();
    const [busy, setBusy] = useState<boolean>(false);
    const initialTabs: TabSummary[] = [
        { label: translate.byKey(TranslationKeys.floorGroups), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.lifts), badge: 0, showInitial: true, isActive: true },
    ];
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const floorGroupsLookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.accessTimePattern,
            values: [],
            dataMethod: getAccessTimePatternNames,
            searchAccessor: AccessTimePatternSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.area,
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessAreaNamesWithPagination(null, listQuery),
            searchAccessor: AccessAreaSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.days,
            values: getDayNames(translate)
        }
    ]
    const liftsLookup: Lookup[] = [
        {
            name: LookupTypes.floorBySiteId,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.building,
            values: [],
            dataMethod: getBuildingNamesWithPagination,
            searchAccessor: BuildingSearch.find(item => item.accessor === "name")?.searchOnName
        }
        
    ]

    const fetchTabCounts = async () => {
        try {
            const counts = await getFloorTabCounts(floorId);
            setTabs(currentTabs =>updateTabBadgeTotal(1, counts.liftCount ?? 0,updateTabBadgeTotal(0, counts.floorGroupCount ?? 0, currentTabs)));
        } catch (error) {
            showError(error, toast.warn);
        }
    };

    useEffect(() => {
        fetchTabCounts();
    }, [floorId]);


    const updateTabBadgeTotal = (tabIndex: number, totalCount: number, currentTabs: TabSummary[]) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    };

    const tabContent: ReactElement[] = [
        <AssignedTab
            id={floorId}
            key="floor-groups-tab"
            column={AccessGroupColumns}
            searchColumn={AccessGroupSearch}
            title={translate.byKey(TranslationKeys.assignedFloorGroups)}
            dataMethod={(listParameters?: ListQuery) => getAllAccessGroups(2 ,translate,{...listParameters,filters: appendFilter(floorId, 'Floor', 'floor.id', listParameters?.filters ?? [])})}
            lookup={floorGroupsLookup}>
            {/** Manage button intentionally removed for now*/}
        </AssignedTab>,
        <AssignedTab
            id={floorId}
            key="lifts-tab"
            column={LiftColumns}
            searchColumn={LiftSearch}
            title={translate.byKey(TranslationKeys.assignedLifts)}
            dataMethod={(listParameters?: ListQuery) => getLifts({...listParameters,filters: appendFilter(floorId, 'Floor', 'liftDetails.floor.id', listParameters?.filters ?? [])})}
            lookup={liftsLookup}>
            {/** Manage button intentionally removed for now*/}
        </AssignedTab>
    ];

    const onTabChange = (index: number) => {
        setSelectedTab(index);
    }

    return <ActivityFrame busy={busy} className="flex flex-col flex-grow bg-white">
        <div className="w-full overflow-hidden" >
            <Tabs 
                tabs={tabs}
                selected={selectedTab}
                onSelect={onTabChange}
                add={false}
                onAdd={() => {}}
                isFlat={true}
                className="w-full"
                showBorder={false}>
                <hr/>
                {tabContent[selectedTab]}
            </Tabs>
        </div>
    </ActivityFrame>   
}

export default LinkedToTab;